import { type Job, type FNOL, type Customer, type Property, type Insurance, type EsxProjectInfo } from '@/types/jobs';
import { toCanonicalPhase } from '@/utils/phaseMapping';

/**
 * Returns a unified FNOL view for a job document.
 *
 * iOS writes customer/property at the top level of the job doc, while the
 * MGMT admin panel nests them inside `fnol`. This helper merges both sources
 * so callers always get a consistent shape.
 */
export function getJobFnol(job: Job): Partial<FNOL> {
    const fnol = job.fnol;

    const customer: Partial<Customer> = {
        name: fnol?.customer?.name || job.customer?.name,
        email: fnol?.customer?.email || job.customer?.email,
        phone: fnol?.customer?.phone || job.customer?.phone,
    };

    const property: Partial<Property> = {
        address: fnol?.property?.address || job.property?.address,
        city: fnol?.property?.city || job.property?.city,
        state: fnol?.property?.state || job.property?.state,
        zip: fnol?.property?.zip || job.property?.zip,
        county: fnol?.property?.county || job.property?.county,
    };

    const insurance: Partial<Insurance> = {
        carrier: fnol?.insurance?.carrier || job.insurance?.carrier,
        claimNumber: fnol?.insurance?.claimNumber || job.insurance?.claimNumber,
        policyNumber: fnol?.insurance?.policyNumber || job.insurance?.policyNumber,
        adjuster: {
            name: fnol?.insurance?.adjuster?.name || job.insurance?.adjuster?.name,
            email: fnol?.insurance?.adjuster?.email || job.insurance?.adjuster?.email,
            phone: fnol?.insurance?.adjuster?.phone || job.insurance?.adjuster?.phone,
        },
    };

    return {
        ...fnol,
        customer: customer as Customer,
        property: property as Property,
        insurance: insurance as Insurance,
        lossCategory: fnol?.lossCategory || job.details?.lossCategory,
        lossDescription: fnol?.lossDescription || job.details?.lossDescription,
        notes: fnol?.notes || job.details?.notes,
        lossDate: fnol?.lossDate || job.dates?.lossDate,
        receivedDate: fnol?.receivedDate || job.dates?.fnolReceivedDate,
    };
}

/** Primary display / confirmation label for a job (customer name in FNOL). */
export function getJobDisplayName(job: Job): string {
    const name = getJobFnol(job).customer?.name?.trim();
    return name && name.length > 0 ? name : 'Unknown Customer';
}

/** Job FNOL fields used to enrich ESX download UI when projectInfo is sparse. */
export interface EsxJobContext {
    displayName: string;
    carrier?: string;
    contactName?: string;
    claimNumber?: string;
    phaseType?: string;
}

export function getEsxJobContext(job: Job): EsxJobContext {
    const fnol = getJobFnol(job);
    const phase = toCanonicalPhase(job.phase);
    const adjusterName = fnol.insurance?.adjuster?.name?.trim();
    const customerName = fnol.customer?.name?.trim();

    return {
        displayName: getJobDisplayName(job),
        carrier: fnol.insurance?.carrier?.trim() || undefined,
        contactName: adjusterName || customerName || undefined,
        claimNumber: fnol.insurance?.claimNumber?.trim() || undefined,
        phaseType: phase
            ? phase.charAt(0).toUpperCase() + phase.slice(1)
            : undefined,
    };
}

const displayOrFallback = (value: string | undefined, fallback: string | undefined): string => {
    const primary = value?.trim();
    if (primary) return primary;
    const secondary = fallback?.trim();
    if (secondary) return secondary;
    return 'N/A';
};

export function resolveEsxDisplayFields(
    projectInfo: EsxProjectInfo | undefined,
    jobContext: EsxJobContext,
): {
    displayName: string;
    claimNumber: string;
    contact: string;
    carrier: string;
    type: string;
} {
    return {
        displayName: jobContext.displayName,
        claimNumber: displayOrFallback(projectInfo?.claim_num, jobContext.claimNumber),
        contact: displayOrFallback(
            projectInfo?.contact_name || projectInfo?.claimant_name,
            jobContext.contactName || jobContext.displayName,
        ),
        carrier: displayOrFallback(projectInfo?.carrier_id, jobContext.carrier),
        type: displayOrFallback(projectInfo?.type, jobContext.phaseType),
    };
}
